import * as React from 'react';
import { createTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';

/*
EXAMPLE

<CoreAccordion firstExpandedItemIndex={0} items={[
					{
						title: 'Sample 1',
						description: 'Description 1'
					},
					{
						title: 'Sample 2',
						description: 'Description 2'
					}
				]} style={{
					accordion: (theme) => {
						return {
              'background-color': 'gray' //example of injecting css
						}
					},
					accordionDetails: (theme) => {
						return {
						}
					},
					accordionSummary: (theme) => {
						return {
						}
					},
				}}></CoreAccordion>


*/

const CoreAccordion = (props: CoreAccordionConfig) => {
  const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
  ))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    ...(props.style && props.style.accordion ? props.style.accordion(theme) : null)
  }));

  const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
      {...props}
    />
  ))(({ theme }) => ({

    backgroundColor:
      theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, .05)'
        : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1),
    },
    ...(props.style && props.style.accordionSummary ? props.style.accordionSummary(theme) : null)
  }));

  const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(255, 0, 0, .125)',
    ...(props.style && props.style.accordionDetails ? props.style.accordionDetails(theme) : null)
  }));
  const [expanded, setExpanded] = React.useState<string | false>('panel' + (typeof props.firstExpandedItemIndex === 'undefined' ? 0 : props.firstExpandedItemIndex));

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  return (
    <div>
      {props.items.map((item: CoreAccordionItem, indx: number) => {
        const id = 'panel' + indx;
        return (
          <Accordion expanded={expanded === id} onChange={handleChange(id)}>
            <AccordionSummary aria-controls={id + "d-content"} id={id + "d-header"}>
              <Typography>{item.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                {item.description}
              </Typography>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}

export default CoreAccordion;


export class CoreAccordionConfig {
  firstExpandedItemIndex?: number;
  style?: CoreAccordionStyle;
  items: CoreAccordionItem[];
}

export class CoreAccordionStyle {
  accordion?: (theme: any) => any;
  accordionSummary?: (theme: any) => any;
  accordionDetails?: (theme: any) => any;
}

export class CoreAccordionItem {
  title: string;
  description: string;
}

